import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CloudRain, MagnifyingGlass, CheckCircle, FileText } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;

const ReportVerification = () => {
  const [reportId, setReportId] = useState('');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setReport(null);
    
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/report/verify/${reportId}`);
      setReport(data);
      toast.success('Report verified successfully');
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Report not found');
      } else {
        toast.error('Failed to verify report');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <CloudRain size={32} weight="bold" className="text-blue-600" />
              <span className="text-2xl font-bold tracking-tight text-slate-950" style={{ fontFamily: 'Chivo' }}>WeatherProof</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="mb-12 text-center">
          <h1 className="text-4xl font-bold tracking-tighter text-slate-950 mb-4" style={{ fontFamily: 'Chivo' }}>Verify Report Authenticity</h1>
          <p className="text-sm text-slate-600">Enter a report ID to verify its authenticity and view details</p>
        </div>

        {/* Verification Form */}
        <div className="bg-white border border-slate-200 p-8 mb-8">
          <form onSubmit={handleVerify}>
            <label className="block text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-2">
              Report ID
            </label>
            <div className="flex gap-4">
              <input
                data-testid="report-id-input"
                type="text"
                value={reportId}
                onChange={(e) => setReportId(e.target.value)}
                required
                className="flex-1 px-4 py-3 border border-slate-300 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 focus:outline-none text-sm font-mono"
                placeholder="Enter report ID (e.g., AbCd1234EfGh5678)"
              />
              <button
                data-testid="verify-btn"
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors text-sm font-medium flex items-center gap-2"
              >
                <MagnifyingGlass size={20} weight="bold" />
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          </form>
        </div>

        {/* Verification Result */}
        {report && (
          <div data-testid="verification-result" className="space-y-6">
            {/* Status Badge */}
            <div className="bg-emerald-50 border border-emerald-200 p-6 flex items-center gap-4">
              <CheckCircle size={48} weight="bold" className="text-emerald-600" />
              <div>
                <h2 className="text-xl font-semibold text-emerald-900" style={{ fontFamily: 'Chivo' }}>Report Verified</h2>
                <p className="text-sm text-emerald-700 mt-1">This report was generated by WeatherProof and is authentic</p>
              </div>
            </div>

            {/* Report Details */}
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-4">Report Information</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Report ID</p>
                    <p className="text-sm font-mono text-slate-950">{report.report_id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Generated</p>
                    <p className="text-sm font-mono text-slate-950">{new Date(report.created_at).toLocaleString()}</p>
                  </div>
                </div>
                {report.query && (
                  <>
                    <div className="pt-4 border-t border-slate-200">
                      <h4 className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-3">Query Details</h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Location</p>
                          <p className="text-sm font-medium text-slate-950">{report.query.location}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Date</p>
                          <p className="text-sm font-mono text-slate-950">{report.query.date}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Time</p>
                          <p className="text-sm font-mono text-slate-950">{report.query.time}</p>
                        </div>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-slate-200">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Latitude</p>
                          <p className="text-sm font-mono text-slate-950">{report.query.latitude?.toFixed(6)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Longitude</p>
                          <p className="text-sm font-mono text-slate-950">{report.query.longitude?.toFixed(6)}</p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Disclaimer */}
            <div className="bg-slate-50 border border-slate-200 p-4">
              <p className="text-xs text-slate-600 italic">
                This verification confirms that a report with this ID was generated by WeatherProof. For the complete report data, contact the report holder.
              </p>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="mt-12 p-6 bg-blue-50 border border-blue-200">
          <h3 className="text-sm font-semibold text-blue-900 mb-2" style={{ fontFamily: 'Chivo' }}>About Report Verification</h3>
          <p className="text-xs text-blue-700 leading-relaxed">
            Each WeatherProof report is assigned a unique ID for verification purposes. This system allows anyone to confirm that a report was genuinely generated by our service and view basic metadata about the query. This helps ensure transparency and auditability for professional use cases.
          </p>
        </div>
      </main>
    </div>
  );
};

export default ReportVerification;
